import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, User, MessageSquare } from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactModal = ({ isOpen, onClose }) => {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'VrI8xm6QM5vrS92RU';
    emailjs.init({
      publicKey: publicKey,
    });
  }, []);

  // Lock scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_y6tpd0n';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_ikc9d2g';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'VrI8xm6QM5vrS92RU';
      
      const result = await emailjs.sendForm(
        serviceId,
        templateId,
        form.current,
        {
          publicKey: publicKey,
        }
      );

      console.log('Email envoyé avec succès:', result);
      setSubmitStatus('success');
      
      // Réinitialiser le formulaire après 2 secondes
      setTimeout(() => {
        form.current.reset();
        setSubmitStatus(null);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Erreur EmailJS:', error);
      setSubmitStatus('error');
      setErrorMessage('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-green-500 p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <h2 className="text-3xl font-bold text-white mb-2">Nous contacter</h2>
          <p className="text-white/90">Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.</p>
        </div>

        {/* Form */}
        <form ref={form} onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nom et Prénom */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="last_name" className="block text-sm font-semibold text-gray-700 mb-2">
                Nom *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  required
                  disabled={isSubmitting}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors"
                  placeholder="Votre nom"
                />
              </div>
            </div>

            <div>
              <label htmlFor="first_name" className="block text-sm font-semibold text-gray-700 mb-2">
                Prénom *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  required
                  disabled={isSubmitting}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors"
                  placeholder="Votre prénom"
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="user_email" className="block text-sm font-semibold text-gray-700 mb-2">
              Adresse email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                id="user_email"
                name="user_email"
                required
                disabled={isSubmitting}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors"
                placeholder="votre.email@example.com"
              />
            </div>
          </div>

          {/* Sujet */}
          <div>
            <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
              Sujet (optionnel)
            </label>
            <select
              id="subject"
              name="subject"
              disabled={isSubmitting}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors bg-white"
            >
              <option value="">Choisissez un sujet</option>
              <option value="interclubs">Interclubs</option>
              <option value="juniors">Cours Juniors</option>
              <option value="membership">Devenir membre</option>
              <option value="padel">Padel</option>
              <option value="reservations">Réservations</option>
              <option value="location">Location club-house</option>
              <option value="sponsoring">Sponsoring</option>
              <option value="other">Autre</option>
            </select>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
              Message *
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <textarea
                id="message"
                name="message"
                required
                disabled={isSubmitting}
                rows="5"
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none transition-colors resize-none"
                placeholder="Votre message..."
              />
            </div>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-emerald-50 border-2 border-emerald-500 text-emerald-700 p-4 rounded-lg">
              ✅ Message envoyé avec succès ! Nous vous répondrons bientôt.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border-2 border-red-500 text-red-700 p-4 rounded-lg">
              ❌ {errorMessage}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-wrap gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white px-8 py-4 rounded-lg font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-8 py-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
